import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert,
} from '@mui/material'
import { Add, MoreVert, Edit, RuleFolder, ShoppingCartCheckout } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listPurchaseRequests, submitPurchaseRequestForApproval, type PurchaseRequestListItem, type PurchaseRequestStatus,
} from '../../api/achizitiiCereri'
import { errorMessage } from '../../api/client'
import PurchaseRequestFormDialog from './PurchaseRequestFormDialog'
import ConvertRequestToOrderDialog from './ConvertRequestToOrderDialog'

const STATUS_LABEL: Record<PurchaseRequestStatus, string> = {
  Draft: 'Ciornă', PendingApproval: 'În aprobare', Approved: 'Aprobată', Rejected: 'Respinsă', ConvertedToOrder: 'Convertită',
}
const STATUS_COLOR: Record<PurchaseRequestStatus, 'default' | 'success' | 'warning' | 'error'> = {
  Draft: 'default', PendingApproval: 'warning', Approved: 'success', Rejected: 'error', ConvertedToOrder: 'success',
}

export default function PurchaseRequestsPage() {
  const [formTarget, setFormTarget] = useState<PurchaseRequestListItem | null | 'new'>(null)
  const [convertTarget, setConvertTarget] = useState<PurchaseRequestListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: PurchaseRequestListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['achizitii', 'cereri'], queryFn: listPurchaseRequests })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['achizitii', 'cereri'] })

  const submitMutation = useMutation({
    mutationFn: submitPurchaseRequestForApproval,
    onSuccess: (r) => { invalidate(); setError(r.pendingApproval ? 'Cerere trimisă la aprobare.' : null) },
    onError: (e) => setError(errorMessage(e)),
  })

  const columns = useMemo<GridColDef<PurchaseRequestListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 130, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'requestedDate', headerName: 'Data', width: 110 },
    { field: 'requestedByUserName', headerName: 'Solicitant', width: 160 },
    { field: 'department', headerName: 'Departament', width: 150, valueGetter: (v: string | null) => v ?? '' },
    { field: 'estimatedTotal', headerName: 'Total estimat', width: 130, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    {
      field: 'status', headerName: 'Status', width: 150, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Achiziții</Typography>
        <Typography variant="inherit" color="text.primary">Cereri de achiziție</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Cereri de achiziție</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Cerere nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <PurchaseRequestFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <ConvertRequestToOrderDialog request={convertTarget} onClose={() => setConvertTarget(null)} onConverted={invalidate} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" disabled={menuAnchor.row.status !== 'Draft'} onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="submit" disabled={menuAnchor.row.status !== 'Draft'}
            onClick={() => { submitMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><RuleFolder fontSize="small" /></ListItemIcon><ListItemText>Trimite la aprobare</ListItemText>
          </MenuItem>,
          <MenuItem key="convert" disabled={menuAnchor.row.status !== 'Approved'}
            onClick={() => { setConvertTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><ShoppingCartCheckout fontSize="small" /></ListItemIcon><ListItemText>Convertește în comandă</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity={error?.includes('aprobare') ? 'info' : 'error'} onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
